import React, { useEffect, useMemo, useState } from "react";
import _ from "lodash";
import { Button, FormLayout, Heading, Input, Select, Switch } from "@/ui";
import { useForm, useValidator } from "@/hooks";
import styles from "./SwifterForm.module.scss";

const fieldError = (validator, name) =>
    validator?.[name]?.isInvalid ? validator[name].message : undefined;

const SwifterCredentialForm = ({
    isCreate,
    handleCancel,
    app,
    stores,
    selectCredential,
    setCredentials,
    isUpdating,
    handlerUpdatingOrCreate,
}) => {
    const [isFormReset, resetFormInputs] = useState(isCreate);
    const { inputs, inputChange, setInputs } = useForm();

    const { validator, isValid, isSubmited } = useValidator(
        [
            {
                field: "swifter_store_provider_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Store provider id is required.",
            },
            {
                field: "swifter_client_id",
                method: "isEmpty",
                validWhen: false,
                message: "This Client Id is required.",
            },
            {
                field: "swifter_client_secret",
                method: "isEmpty",
                validWhen: false,
                message: "This Client Secret is required.",
            },
            {
                field: "swifter_auth_host",
                method: "isEmpty",
                validWhen: false,
                message: "This Auth Host is required.",
            },
            {
                field: "swifter_api_host",
                method: "isEmpty",
                validWhen: false,
                message: "This Api Host is required.",
            },
        ],
        {
            ...inputs,
        },
    );

    const storeOptions = useMemo(
        () =>
            (stores || []).map(store => ({
                value: String(store.value),
                label: store.label,
            })),
        [stores],
    );

    useEffect(() => {
        if (!isCreate) {
            const objSelect = app?.credentials[selectCredential];
            setInputs({
                swifter_sandbox: objSelect?.swifter_sandbox,
                swifter_store_provider_id: objSelect?.swifter_store_provider_id,
                swifter_client_id: objSelect?.swifter_client_id,
                swifter_client_secret: objSelect?.swifter_client_secret,
                swifter_auth_host: objSelect?.swifter_auth_host,
                swifter_api_host: objSelect?.swifter_api_host,
            });
        }
    }, []);

    if (!isCreate && !isFormReset) {
        resetFormInputs(true);
    }

    const createOrUpdate = async () => {
        if (isSubmited) {
            isSubmited(true);
        }

        if (isValid()) {
            const accounts = _.cloneDeep(app.credentials ?? []);

            if (isCreate) {
                accounts.push({
                    swifter_sandbox: inputs?.swifter_sandbox,
                    swifter_store_provider_id:
                        inputs?.swifter_store_provider_id,
                    swifter_client_id: inputs?.swifter_client_id,
                    swifter_client_secret: inputs?.swifter_client_secret,
                    swifter_auth_host: inputs?.swifter_auth_host,
                    swifter_api_host: inputs?.swifter_api_host,
                });
            } else {
                accounts[selectCredential] = inputs;
            }
            setCredentials(accounts);
            handlerUpdatingOrCreate(accounts);
        }
    };

    const handleClose = () => {
        handleCancel();
        resetFormInputs(false);
    };

    const handleStoreProviderChange = value => {
        const store = (stores || []).find(s => String(s.value) === value);
        setInputs({
            ...inputs,
            swifter_store_provider_id: store ? store.value : value,
        });
    };

    return (
        <div className={styles.credentialForm}>
            <Heading level={5}>
                {isCreate ? "Add Credential" : "Update Credential"}
            </Heading>

            <FormLayout>
                <FormLayout.Section title="Credential Informations">
                    <Switch
                        checked={Boolean(inputs.swifter_sandbox)}
                        label="Sandbox"
                        name="swifter_sandbox"
                        onChange={inputChange}
                    />
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Select
                            error={fieldError(
                                validator,
                                "swifter_store_provider_id",
                            )}
                            fullWidth
                            label="Store Provider Id"
                            onValueChange={handleStoreProviderChange}
                            options={storeOptions}
                            value={
                                inputs.swifter_store_provider_id != null
                                    ? String(inputs.swifter_store_provider_id)
                                    : undefined
                            }
                        />
                    </FormLayout.Grid>
                    <FormLayout.Grid columns={2} maxWidth="twoThirds">
                        <Input
                            error={fieldError(validator, "swifter_client_id")}
                            fullWidth
                            label="Client id"
                            name="swifter_client_id"
                            onChange={inputChange}
                            placeholder="Client Id"
                            value={inputs.swifter_client_id || ""}
                        />
                        <Input
                            error={fieldError(
                                validator,
                                "swifter_client_secret",
                            )}
                            fullWidth
                            label="Client secret"
                            name="swifter_client_secret"
                            onChange={inputChange}
                            placeholder="Client Secret"
                            value={inputs.swifter_client_secret || ""}
                        />
                        <Input
                            error={fieldError(validator, "swifter_api_host")}
                            fullWidth
                            label="Api host"
                            name="swifter_api_host"
                            onChange={inputChange}
                            placeholder="Api Host"
                            value={inputs.swifter_api_host || ""}
                        />
                        <Input
                            error={fieldError(validator, "swifter_auth_host")}
                            fullWidth
                            label="Auth host"
                            name="swifter_auth_host"
                            onChange={inputChange}
                            placeholder="Auth Host"
                            value={inputs.swifter_auth_host || ""}
                        />
                    </FormLayout.Grid>
                </FormLayout.Section>
            </FormLayout>

            <div className={styles.credentialActions}>
                <Button
                    disabled={isUpdating}
                    onClick={handleClose}
                    type="button"
                    variant="secondary"
                >
                    Cancel
                </Button>
                <Button
                    loading={isUpdating}
                    onClick={createOrUpdate}
                    type="button"
                >
                    Save
                </Button>
            </div>
        </div>
    );
};

export default SwifterCredentialForm;
